import { DefaultAvatar } from '@/app/pages/profile/components/DefaultAvatar'
import Badge from '@/common/components/Badge'

import {
  ProviderUser,
  ProviderUserRole,
  ProviderUserRoleCode,
  ProviderUserStatus,
} from '@/queries/provider-permission-management/schema'
import { selectSelectedProgram } from '@/storage/programs/slice'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  CrownIcon,
  DollarSignIcon,
  EyeIcon,
  LucideIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import EditUserDialog from '../../manage-user-form/edit-user-dialog'
import UserActionsMenu from './user-actions-menu'
import { formatInTimeZone } from 'date-fns-tz'
import { DATE_FNS_DEFAULT_DATE_FORMAT } from '@/lib/utils'

interface ManagementTableProps {
  users: ProviderUser[]
  providerId: string
  roles: ProviderUserRole[]
  searchFilter: string
}

const ROLE_CONFIG: Record<ProviderUserRoleCode, { icon: LucideIcon }> = {
  OWNER: { icon: CrownIcon },
  ADMIN: { icon: ShieldCheckIcon },
  FINANCE: { icon: DollarSignIcon },
  GENERAL: { icon: UserIcon },
  'READ-ONLY': { icon: EyeIcon },
} as const

const columnHelper = createColumnHelper<ProviderUser>()

export default function ProviderUsersTable({
  users,
  providerId,
  roles,
  searchFilter,
}: ManagementTableProps) {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const programId = useSelector(selectSelectedProgram).id

  const handleEditUserClick = (userId: number) => {
    setSelectedUserId(userId)
  }

  const columns = [
    columnHelper.accessor((row) => `${row.name} ${row.email}`, {
      id: 'name',
      header: () => (
        <>
          <span className="text-sm text-gray-800">Name</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center gap-2">
            <DefaultAvatar
              firstName={row.first_name}
              lastName={row.last_name}
            />
            <div className="font-bold ">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <div>
                  <div className="text-sm text-gray-800">{row.name}</div>
                  <div className="text-gray-600 text-sm font-normal">
                    {row.email}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      },
    }),

    columnHelper.accessor('role.name', {
      header: () => <span className="text-sm text-gray-800">Role</span>,
      cell: (info) => {
        const row = info.row.original
        const Icon = ROLE_CONFIG[row.role.code].icon

        return (
          <div>
            <Badge semanticColor="Neutral">
              <Icon className="size-4 mr-2" />
              {info.getValue()}
            </Badge>
          </div>
        )
      },
    }),
    columnHelper.accessor('status_name', {
      header: () => <span className="text-sm text-gray-800">Status</span>,
      cell: (info) => {
        const status = info.getValue()

        function getBadgeColor(status: ProviderUserStatus) {
          if (status === 'Active') return 'Positive'
          if (status === 'Inactive') return 'Negative'
          if (status === 'Pending') return 'Notice'
          return 'Neutral'
        }

        return (
          <div>
            <Badge semanticColor={getBadgeColor(status)}>
              {info.getValue()}
            </Badge>
          </div>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      header: () => <span className="text-sm text-gray-800">Created At</span>,
      cell: (info) => {
        const value = info.getValue()

        const formatted = formatInTimeZone(
          value,
          'UTC',
          DATE_FNS_DEFAULT_DATE_FORMAT
        )

        return (
          <div className="text-gray-600 text-sm font-normal">
            {formatted} (UTC)
          </div>
        )
      },
    }),
    columnHelper.accessor('last_login_at', {
      header: () => <span className="text-sm text-gray-800">Last Login</span>,
      cell: (info) => {
        const value = info.getValue() ?? 'Never'
        return <div className="text-gray-600 text-sm font-normal">{value}</div>
      },
    }),

    columnHelper.display({
      id: 'action',
      header: () => (
        <span className="flex justify-end text-sm text-gray-800">Action</span>
      ),
      cell: (info) => {
        const row = info.row.original

        return (
          <UserActionsMenu
            user={row}
            programId={programId}
            onEditUser={handleEditUserClick}
            providerId={providerId}
          />
        )
      },
    }),
  ]

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter: searchFilter,
    },
  })

  const hasNoData = table.getRowModel().rows.length === 0

  const selectedContact = users.find((user) => user.id === selectedUserId)
  const currentOwner = users.find((user) => user.role.code === 'OWNER') ?? null

  return (
    <>
      {hasNoData ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="rounded-full bg-gray-100 p-4 mb-4">
            <UsersIcon className="size-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No users found
          </h3>
          <p className="text-sm text-gray-600 text-center max-w-sm">
            {searchFilter
              ? 'No users match your search criteria. Try adjusting your search.'
              : 'There are no users assigned to this provider yet.'}
          </p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="text-left font-medium tracking-tight px-4"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="text-left py-4 px-4 ">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedContact && (
        <EditUserDialog
          roles={roles}
          selectedContact={selectedContact}
          currentOwner={currentOwner}
          onOpenChange={() => {
            setSelectedUserId(null)
          }}
          providerId={providerId}
        />
      )}
    </>
  )
}
